// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AppEvent } from '@interfaces/interfaces';
import { EnrollmentService } from '@services/enrollment.service';
import { debounceTime, takeWhile } from 'rxjs';
import { SuccessModalComponent } from 'src/app/modals/success-modal/success-modal.component';
import { emailPattern, namePattern, phoneExporter, usPhonePattern } from 'src/app/utils/form-utils';
import { DealershipService } from '../../../services/dealership.service';
import { SettingsService } from '@services/settings.service';

@Component({
    selector: 'app-token-expired',
    templateUrl: './token-expired.component.html',
    styleUrls: ['./token-expired.component.scss'],
})
export class TokenExpiredComponent implements OnInit {
    @Input() token: string;
    @Input() dealerCode: string;
    @Output() signal: EventEmitter<AppEvent> = new EventEmitter();
    requestAccessForm: FormGroup;
    isLoading = false;
    isComponentAlive = true;
    tenantSupportEmail = '';
    tenantSupportPhone = '';

    constructor(
        private fb: FormBuilder,
        private readonly enrollmentService: EnrollmentService,
        private readonly dialog: MatDialog,
        private readonly router: Router,
        private readonly dealershipService: DealershipService,
        private readonly settingsService: SettingsService
    ) {}

    ngOnInit(): void {
        const updateOemSettings = async (oemCode) => {
            const data = await this.settingsService.getOemSettings(oemCode);
            this.tenantSupportEmail = data.supportEmail;
            this.tenantSupportPhone = data.supportPhone;
        };
        this.dealershipService.dealer$.pipe(takeWhile(() => this.isComponentAlive)).subscribe(async (dealer) => {
            await updateOemSettings(dealer.oemCode);
        });
        this.settingsService.oemCodeEvent().subscribe(async (oemCode) => {
            await updateOemSettings(oemCode);
        });

        this.buildForm();

        this.requestAccessForm.controls.email.valueChanges
            .pipe(
                takeWhile(() => this.isComponentAlive),
                debounceTime(500)
            )
            .subscribe((value) => {
                value.match(/\s+$/) && this.requestAccessForm.controls.email.setValue(value.trim());
            });
    }

    cancel() {
        this.signal.emit({ context: 'cancel', data: null });
    }

    buildForm() {
        this.requestAccessForm = this.fb.group({
            email: ['', Validators.compose([Validators.required, Validators.maxLength(500), Validators.pattern(emailPattern)])],
            firstName: ['', Validators.compose([Validators.required, Validators.maxLength(128), Validators.pattern(namePattern)])],
            lastName: ['', Validators.compose([Validators.required, Validators.maxLength(128), Validators.pattern(namePattern)])],
            mobilePhone: ['', Validators.compose([Validators.required, Validators.pattern(usPhonePattern)])],
        });
    }

    submit() {
        if (!this.requestAccessForm.valid) {
            return;
        }

        this.isLoading = true;

        const email = this.requestAccessForm.get('email').value;
        const firstName = this.requestAccessForm.get('firstName').value;
        const lastName = this.requestAccessForm.get('lastName').value;
        const mobilePhone = phoneExporter(this.requestAccessForm.get('mobilePhone').value);
        this.enrollmentService.requestAccess(this.token, firstName, lastName, email, mobilePhone).subscribe((success) => {
            this.isLoading = false;

            this.dialog
                .open(SuccessModalComponent, {
                    width: '700px',
                    closeOnNavigation: true,
                    data: {
                        title: 'Access Request Sent',
                        content: `An email has been sent to your system administrator(s).
<br /><br /><br /><strong>If you have never used EZ DOCS</strong>: A system administrator will create a profile for you at this location and send you a Welcome Message to complete your account set up.
<br /><br /><strong>If you have access to EZ DOCS for another location</strong>: A system administrator will add you to this location and you will have access to it the next time you log in.`,
                        btnText: 'Close',
                    },
                })
                .afterClosed()
                .subscribe(() => {
                    this.router.navigate(['/login']);
                });
        });
    }
}
